import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EpisodeCard from "@/components/feature/EpisodeCard";
import Pagination from "@/components/feature/Pagination";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";

const PAGE_SIZE = 20;

const ranges = [
  { value: "all", label: "全部集数", min: 1, max: 12 },
  { value: "1-4", label: "第 1 - 4 集", min: 1, max: 4 },
  { value: "5-8", label: "第 5 - 8 集", min: 5, max: 8 },
  { value: "9-12", label: "第 9 - 12 集", min: 9, max: 12 },
];

export default function Episodes() {
  const [searchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") ?? "";

  const [keyword, setKeyword] = useState(urlKeyword);
  const [range, setRange] = useState("all");
  const [sort, setSort] = useState<"desc" | "asc">("desc");
  const [page, setPage] = useState(1);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setKeyword(urlKeyword);
    setPage(1);
  }, [urlKeyword]);

  const filtered = useMemo(() => {
    const activeRange = ranges.find((item) => item.value === range) ?? ranges[0];
    const term = keyword.trim().toLowerCase();
    const list = episodes.filter((episode) => {
      const inRange = episode.number >= activeRange.min && episode.number <= activeRange.max;
      const matchesKeyword =
        !term ||
        episode.title.toLowerCase().includes(term) ||
        episode.summary.toLowerCase().includes(term) ||
        episode.tags.some((tag) => tag.toLowerCase().includes(term));
      return inRange && matchesKeyword;
    });
    return list.sort((a, b) =>
      sort === "desc"
        ? b.releaseDate.localeCompare(a.releaseDate)
        : a.releaseDate.localeCompare(b.releaseDate)
    );
  }, [keyword, range, sort]);

  const relatedCharacters = useMemo(() => {
    const term = keyword.trim().toLowerCase();
    if (!term) return [];
    return characters.filter(
      (character) =>
        character.name.toLowerCase().includes(term) ||
        character.tagline.toLowerCase().includes(term) ||
        character.role.includes(term)
    );
  }, [keyword]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetAll = () => {
    setKeyword("");
    setRange("all");
    setSort("desc");
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <title>甜蜜惩罚全集剧情列表 - 甜蜜惩罚动漫</title>
      <meta
        name="description"
        content="甜蜜惩罚动漫全集剧情列表，逐集整理第 1 至 12 集剧情摘要与发布日期，支持按集数区间筛选、按播出时间排序与关键词搜索。"
      />
      <meta name="keywords" content="甜蜜惩罚全集,甜蜜惩罚剧情列表,甜蜜惩罚分集剧情,甜蜜惩罚第几集" />
      <link rel="canonical" href="https://cloudschoolserver.cn/episodes" />

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "全剧情" }]} />

      <header className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8">
        <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">全集剧情</h1>
        <p className="text-sm md:text-base text-foreground-600 leading-loose font-label max-w-3xl">
          这里收录了《甜蜜惩罚》全十二集的剧情概览。你可以按集数区间筛选、按播出时间排序，
          也可以用关键词检索某一段剧情。点击任意卡片即可查看该集的详细剧情介绍与相关推荐。
        </p>
      </header>

      <section className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
        <div className="flex items-center justify-between gap-3 mb-4 lg:mb-4">
          <div className="flex items-center gap-2 text-sm font-label text-foreground-700">
            <i className="ri-equalizer-line text-primary-600"></i>
            共 <strong className="text-primary-700">{filtered.length}</strong> 集符合条件
          </div>
          <button
            type="button"
            onClick={() => setFiltersOpen((prev) => !prev)}
            className="lg:hidden inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-100 text-sm font-label text-foreground-700 cursor-pointer whitespace-nowrap"
          >
            <i className={filtersOpen ? "ri-close-line" : "ri-filter-3-line"}></i>
            {filtersOpen ? "收起筛选" : "筛选条件"}
          </button>
        </div>

        <div className={`${filtersOpen ? "grid" : "hidden"} lg:grid grid-cols-1 md:grid-cols-3 gap-4`}>
          <div>
            <label className="block text-xs font-label text-foreground-500 mb-1.5" htmlFor="ep-range">
              集数范围
            </label>
            <select
              id="ep-range"
              value={range}
              onChange={(event) => {
                setRange(event.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 font-label focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              {ranges.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-label text-foreground-500 mb-1.5" htmlFor="ep-sort">
              发布日期排序
            </label>
            <select
              id="ep-sort"
              value={sort}
              onChange={(event) => {
                setSort(event.target.value as "desc" | "asc");
                setPage(1);
              }}
              className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 font-label focus:outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="desc">最新发布优先（降序）</option>
              <option value="asc">最早发布优先（升序）</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-label text-foreground-500 mb-1.5" htmlFor="ep-search">
              搜索标题关键词
            </label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
              <input
                id="ep-search"
                type="search"
                value={keyword}
                onChange={(event) => {
                  setKeyword(event.target.value);
                  setPage(1);
                }}
                placeholder="如：告白 / 雨夜"
                className="w-full pl-9 pr-3 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 font-label focus:outline-none focus:border-primary-400"
              />
            </div>
          </div>
        </div>
      </section>

      {paged.length === 0 ? (
        <div className="rounded-lg bg-background-50 border border-background-200 p-12 text-center">
          <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400 mb-4">
            <i className="ri-search-eye-line"></i>
          </span>
          <h2 className="font-heading text-lg text-foreground-950 mb-2">没有找到符合条件的剧情</h2>
          <p className="text-sm text-foreground-500 font-label mb-5">
            试试更换关键词，或放宽集数范围筛选条件。
          </p>
          <button
            type="button"
            onClick={resetAll}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i> 重置全部筛选
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {paged.map((episode, index) => (
              <EpisodeCard key={episode.id} episode={episode} index={index} />
            ))}
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {relatedCharacters.length > 0 && (
        <section className="rounded-lg bg-accent-50 border border-accent-200 p-5 md:p-6">
          <h2 className="font-heading text-lg text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-user-search-line text-accent-700"></i>
            与「{keyword.trim()}」相关的角色
          </h2>
          <div className="flex flex-wrap gap-2">
            {relatedCharacters.map((character) => (
              <Link
                key={character.id}
                to={`/character/${character.id}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50 border border-accent-200 text-sm font-label text-foreground-800 hover:border-accent-400 transition-colors cursor-pointer"
              >
                <i className="ri-user-star-line text-accent-700"></i>
                {character.name}
                <span className="text-xs text-foreground-400">{character.role}</span>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}